import { useCallback } from 'react';
import { driver, type Config, type DriveStep } from 'driver.js';
import { useMembership } from '@/hooks/useMembership';

const TOUR_COMPLETED_KEY = 'hiponhero_tour_completed';

const supervisorTourSteps: DriveStep[] = [
  {
    element: '[data-tour="sidebar-ponds"]',
    popover: {
      title: 'Farm Overview',
      description: 'See all your ponds at a glance with current status indicators',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="alerts-badge"]',
    popover: {
      title: 'Alerts',
      description: 'Active alerts appear here. Click to view details and acknowledge.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="pond-card"]',
    popover: {
      title: 'Pond Cards',
      description: 'Click any pond to see detailed water quality, feeding, and sampling data.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="chart-tabs"]',
    popover: {
      title: 'Water Quality Trends',
      description: 'Switch between parameters to see historical trends.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-tour="edit-requests"]',
    popover: {
      title: 'Edit Requests',
      description: 'Approve or reject edit requests from Lab Technicians here.',
      side: 'right',
      align: 'start',
    },
  },
];

const technicianTourSteps: DriveStep[] = [
  {
    element: '[data-tour="today-checklist"]',
    popover: {
      title: "Today's Checklist",
      description: 'Your daily tasks. Tap a pond to start entering readings.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="pond-status"]',
    popover: {
      title: 'Pond Status',
      description: 'Green = complete, Yellow = partial, Gray = not started',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="sync-button"]',
    popover: {
      title: 'Sync Data',
      description: 'Click to sync your data with the server.',
      side: 'bottom',
      align: 'center',
    },
  },
];

interface LaunchTourOptions {
  isTechnician: boolean;
  onComplete?: () => void;
}

function getAvailableSteps(isTechnician: boolean): DriveStep[] {
  const tourSteps = isTechnician ? technicianTourSteps : supervisorTourSteps;
  return tourSteps.filter((step) => {
    if (!step.element) return true;
    const el = document.querySelector(step.element as string);
    // The desktop sidebar stays in the DOM under `display: none` below 1024px;
    // a bare existence check would anchor its steps to a hidden element.
    return el !== null && el.getClientRects().length > 0;
  });
}

export function launchTour({ isTechnician, onComplete }: LaunchTourOptions): void {
  const availableSteps = getAvailableSteps(isTechnician);
  if (availableSteps.length === 0) {
    return;
  }

  const driverConfig: Config = {
    showProgress: true,
    steps: availableSteps,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    onDestroyed: () => {
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      onComplete?.();
    },
  };

  const driverObj = driver(driverConfig);
  setTimeout(() => {
    driverObj.drive();
  }, 500);
}

export function hasTourCompleted(): boolean {
  return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
}

export function resetTour(): void {
  localStorage.removeItem(TOUR_COMPLETED_KEY);
}

export function useTour() {
  const { membership } = useMembership();
  const isTechnician = membership?.role === 'technician';

  const startTour = useCallback(() => {
    launchTour({ isTechnician });
  }, [isTechnician]);

  const replayTour = useCallback(() => {
    resetTour();
    startTour();
  }, [startTour]);

  return { startTour, replayTour, hasTourCompleted };
}
